import { CellTable } from "@/atoms"
import { getDefaultColumnParams } from "@/helpers/table.helpers"
import { HeaderCell } from "@/molecules/HeaderCell"
import { TrendCell } from "@/pages/DeploymentsPage/components/molecules/TrendCell"
import { DeploymentTypeCell } from "../../molecules/DeploymentTypeCell"
import { KortexVersionCell } from "../../molecules/KortexVersionCell"
import { StatusCell } from "../../molecules/StatusCell"
import { CloudConnectionSettingsCell } from "./CloudConnectionSettingsCell"

import styles from "./DeploymentsTab.module.scss"

import type { SortOrder } from "@/atoms"
import type { NumberConvertor } from "@/hooks/useNumberConvertor"
import type { ORDER_BY_PROPS } from "@/hooks/useOrderBy"
import type { TFunction } from "@/hooks/useTranslation"

export const getColumnsTable = (
  sorter: Omit<ORDER_BY_PROPS, "onSorterChange">,
  attr: {
    t: TFunction
    convertor:
      NumberConvertor["abbreviateNumber"] | NumberConvertor["intlNumber"]
    // A table without cloud connections keeps its columns as they were
    hasCloudConnections?: boolean
  }
) => [
  {
    title: "Environment Name",
    width: "25%",
    sortDirections: ["ascend", "descend"] as SortOrder[],
    render: CellTable,
    ...getDefaultColumnParams("environmentName", sorter)
  },
  {
    title: "Version",
    width: "18%",
    sortDirections: ["ascend", "descend"] as SortOrder[],
    render: KortexVersionCell(attr.t),
    ...getDefaultColumnParams("kortexVersion", sorter)
  },
  {
    title: "Deployment Type",
    width: "18%",
    sortDirections: ["ascend", "descend"] as SortOrder[],
    render: DeploymentTypeCell,
    ...getDefaultColumnParams("deploymentType", sorter)
  },
  {
    title: "Status",
    width: "10%",
    sortDirections: ["descend", "ascend"] as SortOrder[],
    render: StatusCell(attr.t),
    ...getDefaultColumnParams("status", sorter)
  },
  {
    title: "First Installation",
    width: "13%",
    sortDirections: ["descend", "ascend"] as SortOrder[],
    render: CellTable,
    ...getDefaultColumnParams("firstInstallationTime", sorter)
  },
  {
    title: "Last Upgrade",
    width: "11%",
    sortDirections: ["descend", "ascend"] as SortOrder[],
    render: CellTable,
    ...getDefaultColumnParams("lastUpgradeTime", sorter)
  },
  {
    title: HeaderCell({
      title: "Avg. Sensors",
      Content:
        "Average number of live sensors per hour over the last 30 days, compared with the 30 days before. Hours without live sensors count as zero."
    }),
    colSpan: 2,
    width: "70px",
    render: TrendCell(attr.convertor, "value"),
    ...getDefaultColumnParams("avg")
  },
  {
    render: TrendCell(attr.convertor, "percentage"),
    colSpan: 0,
    width: "50px",
    className: styles.trendPercentage,
    ...getDefaultColumnParams("avg")
  },
  // A cloud connection's settings, at the end of its row
  ...(attr.hasCloudConnections
    ? [
        {
          width: "48px",
          render: CloudConnectionSettingsCell(attr.t),
          ...getDefaultColumnParams("settings")
        }
      ]
    : [])
]
